import * as React from 'react';
import BPromise from 'bluebird';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { parse } from 'query-string';
import { History, Location } from 'history';
import {
  ColumnDefinition,
  getFilters2,
  getOrder,
  getSearchFilter,
  getSearchOrder,
  getServerFieldSectionWhere,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import { FILTER_TYPE, FIRST_PAGE } from 'Src/consts/table';
import { Loader } from 'Src/components/Loader/Loader';
import { Pagination2 } from 'Src/components/Pagination/Pagination2';
import { SelectAllCheckbox } from 'Src/components/SelectAllCheckbox/SelectAllCheckbox';
import { getEmailOrMisEmail, getPhoneOrMisPhone } from 'Src/helpers/accessor/accessor';
import {
  getAllSchoolsAdmins,
  getSchoolsAdmins,
  getSchoolsAdminsCount
} from 'Src/helpers/service/adminSU/schoolsAdminsTable';
import { getAllSchoolUnionSchools } from 'Src/helpers/service/adminSU/schoolsTable';
import {
  countActivePeopleFilters,
  PeopleFilterChips
} from '../../AdminView/Users/PeopleFilterChips';
import '../../AdminView/Users/Students/Students.scss';
import '../../AdminView/Users/Staffs/Staffs.css';
import './SchoolsAdmins.scss';
import { ListEmptyState } from 'Src/components/ListEmptyState/ListEmptyState';
import { faUsersCog } from '@fortawesome/free-solid-svg-icons';

const COLUMNS: ColumnDefinition[] = [
  { text: 'Name', field: 'firstName', isSort: true, type: FILTER_TYPE.NONE, accessor: ['firstName'] },
  { text: 'Surname', field: 'lastName', isSort: true, type: FILTER_TYPE.NONE, accessor: ['lastName'] },
  { text: 'Email', field: 'email', isSort: true, type: FILTER_TYPE.TEXT, accessor: getEmailOrMisEmail },
  { text: 'Phone', field: 'phone', isSort: true, type: FILTER_TYPE.TEXT, accessor: getPhoneOrMisPhone },
  { text: 'Schools', field: 'schools', isSort: false, type: FILTER_TYPE.NONE, accessor: ['permissions'] }
];

const FILTER_COLUMNS: ColumnDefinition[] = [
  ...COLUMNS,
  { text: 'Search', field: 'nameSearch', isSort: false, type: FILTER_TYPE.TEXT, accessor: ['firstName'] },
  { text: 'School', field: 'schoolSearch', isSort: false, type: FILTER_TYPE.TEXT, accessor: ['permissions'] }
];

interface State {
  items: any[];
  currentPage: number;
  selectedItems: any[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  filters: any;
  isDataFiltered: boolean;
  isLoading: boolean;
  total: number;
  basePath: string;
  schools: any[];
  previewAdmin: any | null;
  hiddenColumnKeys: string[];
  isColumnsMenuOpen: boolean;
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

export class SchoolsAdmins extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      items: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      filters: {},
      isDataFiltered: false,
      isLoading: true,
      total: 0,
      basePath: props.location.pathname,
      schools: [],
      previewAdmin: null,
      hiddenColumnKeys: this.getInitialHiddenColumnKeys(),
      isColumnsMenuOpen: false
    };
  }

  componentDidMount() {
    document.addEventListener('click', this.onDocumentClick);
    this.setAdditionalItems()
      .then(() => this.setItems())
      .catch(error => {
        console.error('Unable to load school administrators:', error);
        this.setState({ isLoading: false });
      });
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (nextProps.location.search !== this.props.location.search) this.setItems(nextProps);
  }

  getColumnsStorageKey = (): string => `sit.unionSchoolAdmins.columns.${this.props.user.userId}`;

  getInitialHiddenColumnKeys = (): string[] => {
    try {
      return JSON.parse(window.localStorage.getItem(this.getColumnsStorageKey()) || '[]');
    } catch (_error) {
      return [];
    }
  };

  persistHiddenColumnKeys = (hiddenColumnKeys: string[]): void => {
    window.localStorage.setItem(this.getColumnsStorageKey(), JSON.stringify(hiddenColumnKeys));
    this.setState({ hiddenColumnKeys });
  };

  toggleColumn = (field: string): void => {
    const hidden = new Set(this.state.hiddenColumnKeys);
    hidden.has(field) ? hidden.delete(field) : hidden.add(field);
    this.persistHiddenColumnKeys(Array.from(hidden));
  };

  onDocumentClick = (event: MouseEvent): void => {
    if (!this.state.isColumnsMenuOpen) return;
    const target = event.target as HTMLElement | null;
    if (target && target.closest && target.closest('.people-columns')) return;
    this.setState({ isColumnsMenuOpen: false });
  };

  setAdditionalItems = () =>
    getAllSchoolUnionSchools(this.props.user)
      .then(schools => this.setState({ schools: schools || [] }))
      .catch(error => {
        console.error('Unable to load schools for the administrators filter:', error);
        this.setState({ schools: [] });
      });

  getAdminsWhere = (filters: any): any => {
    const { nameSearch, schoolSearch, ...fieldFilters } = filters || {};
    const where = getServerFieldSectionWhere(fieldFilters);
    const conditions: any[] = Array.isArray(where.$and) ? [...where.$and] : [];
    delete where.$and;

    const nameTerm = String(nameSearch || '').trim();
    if (nameTerm) {
      conditions.push({
        $or: [
          { firstName: { like: nameTerm, options: 'i' } },
          { lastName: { like: nameTerm, options: 'i' } }
        ]
      });
    }

    const schoolTerm = String(schoolSearch || '').trim().toLowerCase();
    if (schoolTerm) {
      const schoolIds = this.state.schools
        .filter(school => String(school.name || '').toLowerCase().includes(schoolTerm))
        .map(school => school.id);
      conditions.push({
        permissions: {
          $elemMatch: {
            schoolId: { $in: schoolIds },
            preset: 'ADMIN',
            status: 'ACTIVE'
          }
        }
      });
    }

    if (conditions.length > 0) where.$and = conditions;
    return where;
  };

  setItems = (props: Props = this.props) => {
    const { location, user } = props;
    const search = parse(location.search || '');
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;
    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';
    if (typeof search.order === 'string') [sortColumnsName, sortDirection] = search.order.split(':');

    const filters = getFilters2(FILTER_COLUMNS, search);
    const where = this.getAdminsWhere(filters);
    const order = sortColumnsName && sortDirection ? `${sortColumnsName} ${sortDirection}` : undefined;
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    return BPromise.all([
      getSchoolsAdminsCount(user, where),
      getSchoolsAdmins(user, serverQueryFilter)
    ]).then(([countObj, items]) => {
      this.setState({
        currentPage: page,
        sortDirection,
        sortColumnsName,
        isShowFilter: false,
        isDataFiltered: isFilterExist2(filters),
        filters,
        items,
        total: countObj.count,
        basePath: location.pathname,
        isLoading: false
      });
      return true;
    });
  };

  setCurrentPageParams = (currentPage: number): void => {
    const { filters, sortColumnsName, sortDirection } = this.state;
    const search: string[] = [];
    if (currentPage !== 1) search.push(`page=${currentPage}`);
    if (isSortExist(sortDirection, sortColumnsName)) search.push(getSearchOrder(sortDirection, sortColumnsName));
    if (isFilterExist2(filters)) search.push(getSearchFilter(filters));
    this.props.history.push({ pathname: this.state.basePath, search: search.join('&') });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;
    const order = getOrder(sortField, sortDirection, sortColumnsName);
    const search = [`order=${sortField}:${order}`];
    if (isFilterExist2(filters)) search.push(getSearchFilter(filters));
    this.props.history.push({ pathname: this.state.basePath, search: search.join('&') });
  };

  onModernSearchChange = (field: string, value: string): void => {
    this.setState({ filters: { ...this.state.filters, [field]: value } });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    const search: string[] = [];
    if (isSortExist(sortDirection, sortColumnsName)) search.push(getSearchOrder(sortDirection, sortColumnsName));
    if (isFilterExist2(filters)) search.push(getSearchFilter(filters));
    this.setState({ selectedItems: [], isShowFilter: false });
    this.props.history.push({ pathname: this.state.basePath, search: search.join('&') });
  };

  onCancelFilterClick = (): void => {
    const filters = getFilters2(FILTER_COLUMNS, parse(this.props.location.search || ''));
    this.setState({ filters, isShowFilter: false });
  };

  onClearFilterDraftClick = (): void => {
    this.setState({
      filters: { ...this.state.filters, email: '', phone: '', schoolSearch: '' }
    });
  };

  onClearFilterClick = (): void => {
    this.setState({ selectedItems: [], isShowFilter: false });
    this.props.history.push({ pathname: this.state.basePath, search: '' });
  };

  onRemoveFilter = (field: string): void => {
    this.setState({ filters: { ...this.state.filters, [field]: '' } }, this.onApplyFilterClick);
  };

  onTableFilterClick = (event?: React.SyntheticEvent): void => {
    if (event && event.preventDefault) event.preventDefault();
    this.setState({ isShowFilter: !this.state.isShowFilter });
  };

  getAdminId = (admin: any): string => String(admin.id || admin._id || '');

  toggleAdmin = (admin: any): void => {
    const id = this.getAdminId(admin);
    const isSelected = this.state.selectedItems.some(item => this.getAdminId(item) === id);
    this.setState({
      selectedItems: isSelected
        ? this.state.selectedItems.filter(item => this.getAdminId(item) !== id)
        : [...this.state.selectedItems, admin]
    });
  };

  onSelectAllOnPageClick = (): void => {
    const selectedIds = new Set(this.state.selectedItems.map(this.getAdminId));
    const additions = this.state.items.filter(item => !selectedIds.has(this.getAdminId(item)));
    this.setState({ selectedItems: [...this.state.selectedItems, ...additions] });
  };

  onSelectAllClick = (): void => {
    const where = this.getAdminsWhere(this.state.filters);
    getAllSchoolsAdmins(this.props.user, where).then(items => this.setState({ selectedItems: items || [] }));
  };

  onUnselectAllClick = (): void => this.setState({ selectedItems: [] });

  getAdminSchools = (admin: any): Array<{ id: string; name: string }> => {
    const schoolsById = new Map(this.state.schools.map(school => [String(school.id), school]));
    const unique = new Map<string, { id: string; name: string }>();
    (admin.permissions || []).forEach(permission => {
      const permissionSchool = propz.get(permission, ['school']);
      const schoolId = String(permission.schoolId || propz.get(permissionSchool, ['id'], ''));
      const school = permissionSchool || schoolsById.get(schoolId);
      const name = String(propz.get(school, ['name'], '') || 'School');
      const key = schoolId || name;
      if (!unique.has(key)) unique.set(key, { id: key, name });
    });
    return Array.from(unique.values()).sort((a, b) => a.name.localeCompare(b.name));
  };

  downloadSelectedCsv = (): void => {
    const quote = (value: any) => `"${String(value == null ? '' : value).replace(/"/g, '""')}"`;
    const rows = this.state.selectedItems.map(admin => [
      admin.firstName || '',
      admin.lastName || '',
      getEmailOrMisEmail(admin) || '',
      getPhoneOrMisPhone(admin) || '',
      this.getAdminSchools(admin).map(school => school.name).join('; ')
    ]);
    const csv = [['First name', 'Surname', 'Email', 'Phone', 'Schools'], ...rows]
      .map(row => row.map(quote).join(','))
      .join('\r\n');
    const url = URL.createObjectURL(new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'school-admins.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  renderTable(): React.ReactNode {
    const s = this.state;
    const hidden = new Set(s.hiddenColumnKeys);
    const locked = new Set(['firstName']);
    const visibleColumns = COLUMNS.filter(column => locked.has(column.field) || !hidden.has(column.field));
    const selectedIds = new Set(s.selectedItems.map(this.getAdminId));
    const allPageSelected = s.items.length > 0 && s.items.every(item => selectedIds.has(this.getAdminId(item)));
    const filterCount = countActivePeopleFilters({
      email: s.filters.email,
      phone: s.filters.phone,
      schoolSearch: s.filters.schoolSearch
    });
    const setFilter = (field: string, value: string) =>
      this.setState({ filters: { ...this.state.filters, [field]: value } });
    const fullName = (admin: any) => `${admin.firstName || ''} ${admin.lastName || ''}`.trim() || 'Unnamed user';
    const initials = (admin: any) => `${(admin.firstName || '?')[0]}${(admin.lastName || '')[0] || ''}`.toUpperCase();
    const renderAvatar = (admin: any, large = false) => {
      const avatar = propz.get(admin, ['avatar'], '');
      const classes = `people-avatar${large ? ' people-avatar--lg' : ''}${avatar ? ' staff-avatar--image' : ''}`;
      return avatar
        ? <span className={classes} style={{ backgroundImage: `url(${avatar})` }} />
        : <span className={classes}>{initials(admin)}</span>;
    };
    const cell = (admin: any, field: string) => {
      switch (field) {
        case 'firstName':
          return <span className="staff-nameCell">{renderAvatar(admin)}<strong>{fullName(admin)}</strong></span>;
        case 'lastName': return admin.lastName || '—';
        case 'email': return getEmailOrMisEmail(admin) || '—';
        case 'phone': return getPhoneOrMisPhone(admin) || '—';
        case 'schools': {
          const schools = this.getAdminSchools(admin);
          return <span className="schoolAdminsSchoolChips" title={schools.map(school => school.name).join(', ')}>
            {schools.slice(0, 2).map(school => <span key={school.id}>{school.name}</span>)}
            {schools.length > 2 ? <b>+{schools.length - 2}</b> : null}
          </span>;
        }
        default: return '—';
      }
    };

    return <div className="people-screen staff-modernScreen schoolAdminsScreen">
      <div className="unionAdminsListCard schoolAdminsListCard">
        <div className="people-toolbar">
          <div className="people-toolbar__heading">
            <form className="unionAdminsSearch schoolAdminsSearch" onSubmit={event => { event.preventDefault(); this.onApplyFilterClick(); }}>
              <input
                value={s.filters.nameSearch || ''}
                placeholder="Search by name or surname…"
                onChange={event => this.onModernSearchChange('nameSearch', event.target.value)}
              />
              {s.filters.nameSearch ? <button type="button" aria-label="Clear search" onClick={() => this.setState({ filters: { ...s.filters, nameSearch: '' } }, this.onApplyFilterClick)}>×</button> : null}
            </form>
            <button type="button" className={`people-btn people-btn--secondary people-btn--iconLeft ${s.isShowFilter ? 'people-btn--on' : ''}`} onClick={this.onTableFilterClick}>
              <span className="people-icon people-icon--filter" /> Filters
              {filterCount > 0 ? <span className="people-btn__badge">{filterCount}</span> : null}
            </button>
            <div className="people-columns">
              <button type="button" className="people-btn people-btn--secondary people-btn--iconLeft" onClick={() => this.setState({ isColumnsMenuOpen: !s.isColumnsMenuOpen })}>
                <span className="people-icon people-icon--columns" /> Columns <span className="people-btn__badge">{visibleColumns.length}</span>
              </button>
              {s.isColumnsMenuOpen ? <div className="people-columns__menu">
                <div className="people-columns__head"><span>Show / hide columns</span><button type="button" onClick={() => this.persistHiddenColumnKeys([])}>Reset</button></div>
                {COLUMNS.map(column => <label key={column.field} className={`people-columns__item ${locked.has(column.field) ? 'people-columns__item--locked' : ''}`}>
                  <input type="checkbox" checked={locked.has(column.field) || !hidden.has(column.field)} disabled={locked.has(column.field)} onChange={() => this.toggleColumn(column.field)} />
                  <span>{column.text}</span>
                </label>)}
              </div> : null}
            </div>
          </div>
        </div>

        {s.isShowFilter ? <>
          <div className="unionAdminsFilterScrim" onClick={this.onCancelFilterClick} />
          <form className="people-filter staff-filter" onSubmit={event => { event.preventDefault(); this.onApplyFilterClick(); }}>
            <div className="people-filter__header"><strong>Filters</strong><button type="button" onClick={this.onCancelFilterClick} aria-label="Close filters">×</button></div>
            <div className="people-filter__body">
              <div className="unionAdminsFilterFields">
                <label><span>Email</span><input value={s.filters.email || ''} onChange={event => setFilter('email', event.target.value)} placeholder="Search email…" /></label>
                <label><span>Phone</span><input value={s.filters.phone || ''} onChange={event => setFilter('phone', event.target.value)} placeholder="Search phone…" /></label>
                <label><span>School</span><input value={s.filters.schoolSearch || ''} onChange={event => setFilter('schoolSearch', event.target.value)} placeholder="Search school name…" /></label>
              </div>
            </div>
            <div className="people-filter__actions unionAdminsFilterActions">
              <button type="button" className="people-btn people-btn--ghost" onClick={this.onClearFilterDraftClick}>Clear all</button>
              <div><button type="button" className="people-btn people-btn--secondary" onClick={this.onCancelFilterClick}>Cancel</button><button type="submit" className="people-btn people-btn--primary">Apply</button></div>
            </div>
          </form>
        </> : null}

        <PeopleFilterChips filters={s.filters} columns={FILTER_COLUMNS} valueLabels={{}} onRemove={this.onRemoveFilter} onClear={this.onClearFilterClick} />

        {s.selectedItems.length > 0 ? <div className="people-selection">
          <span className="people-selection__summary">{s.selectedItems.length} selected</span>
          <div className="people-selection__actions"><button type="button" className="people-btn people-btn--secondary" onClick={this.downloadSelectedCsv}>Download selected CSV</button></div>
          <div className="people-toolbar__spacer" />
          <button type="button" className="people-selection__clear" onClick={this.onUnselectAllClick}>Clear selection</button>
        </div> : null}

        <div className="people-tableCard"><div className="people-tableScroll"><table className="people-table staff-modernTable schoolAdminsTable">
          <thead><tr>
            <th className="people-table__check"><SelectAllCheckbox compact={true} isChecked={allPageSelected} totalCount={s.total} currentPage={s.currentPage} selectedItems={s.selectedItems} pageItemsCount={s.items.length} onSelectAllClick={this.onSelectAllClick} onSelectAllOnPageClick={this.onSelectAllOnPageClick} onUnselectAllClick={this.onUnselectAllClick} /></th>
            {visibleColumns.map(column => <th key={column.field} className={column.isSort ? 'people-table__sortable' : ''} onClick={column.isSort ? () => this.onTableSortClick(column.field) : undefined}>
              <span>{column.text}</span>{column.isSort ? <i className={`people-sort ${s.sortColumnsName === column.field ? `people-sort--${s.sortDirection}` : ''}`} /> : null}
            </th>)}
          </tr></thead>
          <tbody>{s.items.length === 0 ? <tr><td className="people-empty" colSpan={visibleColumns.length + 1}>
            <ListEmptyState
              icon={faUsersCog}
              title={s.isDataFiltered ? 'No school admins match your filters' : 'No school admins yet'}
              description={
                s.isDataFiltered
                  ? 'Try changing or clearing the search and filters to see more school admins.'
                  : 'Administrators with access to member schools will appear here.'
              }
            />
          </td></tr> : s.items.map(admin => {
            const id = this.getAdminId(admin);
            return <tr key={id} data-selected={selectedIds.has(id)} data-preview={s.previewAdmin && this.getAdminId(s.previewAdmin) === id} onClick={() => this.setState({ previewAdmin: admin })}>
              <td className="people-table__check" onClick={event => event.stopPropagation()}><input type="checkbox" checked={selectedIds.has(id)} onChange={() => this.toggleAdmin(admin)} aria-label={`Select ${fullName(admin)}`} /></td>
              {visibleColumns.map(column => <td key={column.field}>{cell(admin, column.field)}</td>)}
            </tr>;
          })}</tbody>
        </table></div></div>

        {s.total > 0 ? (
          <Pagination2 modern currentPage={s.currentPage} total={s.total} setCurrentPageParams={this.setCurrentPageParams} />
        ) : null}
      </div>
    </div>;
  }

  renderDrawer(): React.ReactNode {
    const admin = this.state.previewAdmin;
    if (!admin) return null;
    const name = `${admin.firstName || ''} ${admin.lastName || ''}`.trim() || 'Unnamed user';
    const schools = this.getAdminSchools(admin);
    const avatar = propz.get(admin, ['avatar'], '');
    const initials = `${(admin.firstName || '?')[0]}${(admin.lastName || '')[0] || ''}`.toUpperCase();
    return <>
      <div className="people-drawerScrim" onClick={() => this.setState({ previewAdmin: null })} />
      <aside className="people-drawer schoolAdminsDrawer" role="dialog" aria-modal="true" aria-label={`${name} details`}>
        <div className="people-drawer__head">
          {avatar ? <span className="people-avatar people-avatar--lg staff-avatar--image" style={{ backgroundImage: `url(${avatar})` }} /> : <span className="people-avatar people-avatar--lg">{initials}</span>}
          <div className="people-drawer__identity"><h2>{name}</h2><div><span>School administrator</span><i /><span>{getEmailOrMisEmail(admin) || 'No email'}</span></div></div>
          <button type="button" className="people-drawer__close" onClick={() => this.setState({ previewAdmin: null })} aria-label="Close">×</button>
        </div>
        <div className="people-drawer__tabs"><button type="button" className="active">Overview</button></div>
        <div className="people-drawer__body schoolAdminsDrawer__body">
          <section><h3>Contact details</h3><dl><div><dt>Email</dt><dd>{getEmailOrMisEmail(admin) || 'Not provided'}</dd></div><div><dt>Phone</dt><dd>{getPhoneOrMisPhone(admin) || 'Not provided'}</dd></div></dl></section>
          <section><h3>Schools <span>{schools.length}</span></h3>{schools.length > 0 ? <div className="schoolAdminsDrawer__schools">{schools.map(school => <span key={school.id}>{school.name}</span>)}</div> : <p>No member schools are linked to this administrator.</p>}</section>
        </div>
      </aside>
    </>;
  }

  render() {
    if (this.state.isLoading) return <Loader />;
    return <div className="mt-3 staffsPage schoolAdminsPage">
      <header className="unionAdminsPageHeader schoolAdminsPageHeader"><h1>School admins</h1><span>{this.state.total}</span></header>
      {this.renderTable()}
      {this.renderDrawer()}
    </div>;
  }
}
